import { Controller } from "@hotwired/stimulus";
import { togglePopoverHandler } from 'controllers/common/popover_controller';
import { Litepicker } from "litepicker";
import * as http from "utils/http";
import { convertDate } from "utils/time";

const NUMBER_OF_MONTHS = 1;
const MAX_DAYS = 365;
export const DATE_RANGES_BUTTON_ID = 'date-ranges-btn';

export default class extends Controller {
  static values = { identifier: String }
  static targets = ["panel"]

  connect() {
    this.panel = document.getElementById(`${this.identifierValue}-popover-panel`)
    this.panelId = this.identifierValue;
  }

  showDateRangesPopover() {
    const panelId = `${this.identifierValue}-popover-panel`
    togglePopoverHandler(
      document.getElementById(DATE_RANGES_BUTTON_ID),
      document.getElementById(panelId)
    );
    this.fromDateElement = this.panel.querySelector('#from_date');
    this.toDateElement = this.panel.querySelector('#to_date');
    this.showLastElement = this.panel.querySelector('#show_last');
    this.timingsElement = this.panel.querySelector('#timings');
    if (this.fromDateElement && this.toDateElement && !this.pickerObject) {
      this.pickerObject = this._initDatePicker();
    }

    this._subscribeOnApplyDatePickDateRanges();
    this._subscribeOnLastDateApplyDateRanges();
    if (this.showLastElement) {
      this.showLastElement.addEventListener('change', () => {
        this._castShowLast();
        this.validateShowLastInputValue(this.showLastElement, this.timingsElement);
      });
    }

    if (this.timingsElement) {
      this.timingsElement.addEventListener('change', () => {
        this.validateShowLastInputValue(this.showLastElement, this.timingsElement);
      });
    }
  }

  validateShowLastInputValue(showLastInputEl, timingsInputEl) {
    const maxShowLastMinutes = 60;
    const maxShowLastHours = 24;
    const maxShowLastDays = 7;
    const maxShowLastWeeks = 52;

    if (timingsInputEl.value === 'minutes' && showLastInputEl.value > maxShowLastMinutes) {
      showLastInputEl.value = maxShowLastMinutes;

    } else if (timingsInputEl.value === 'hours' && showLastInputEl.value > maxShowLastHours) {
      showLastInputEl.value = maxShowLastHours;

    } else if (timingsInputEl.value === 'days' && showLastInputEl.value > maxShowLastDays) {
      showLastInputEl.value = maxShowLastDays;

    } else if (timingsInputEl.value === 'weeks' && showLastInputEl.value > maxShowLastWeeks) {
      showLastInputEl.value = maxShowLastWeeks;
    }
  }

  async sendQuickFilterData(event) {
    const filterName = event.target.dataset.filterName;

    const response = await http.httpGet("/frontend/forms/date_picker",
      { period: filterName });
    const json = await response.json();
    togglePopoverHandler(
      document.getElementById(DATE_RANGES_BUTTON_ID),
      document.getElementById(`${event.target.dataset.panelId}-popover-panel`)
    );
    const spanElement = document.querySelector(`span[data-filter-type='${event.target.dataset.panelId}']`)
    spanElement.textContent = json['date_interval']
    const fromDateHiddenElement = document.querySelector(`input[name='${event.target.dataset.panelId}[from]']`)
    fromDateHiddenElement.value = json['from_date']
    this._dispatchFieldChangeEvent(fromDateHiddenElement)
    const toDateHiddenElement = document.querySelector(`input[name='${event.target.dataset.panelId}[to]']`)
    toDateHiddenElement.value = json['to_date']
    this._dispatchFieldChangeEvent(toDateHiddenElement)
  }

  async sendDatePickerApplyButtonFilters(from_date, to_date) {
    const response = await http.httpGet("/frontend/forms/date_picker",
      { from_date: from_date, to_date: to_date});
    const json = await response.json();
    togglePopoverHandler(document.getElementById(DATE_RANGES_BUTTON_ID), this.panel);
    this._saveDateTimeInFields(json)
  }

  async sendLastDateApplyButtonFilters(showLast, timings) {
    const response = await http.httpGet("/frontend/forms/date_picker",
      { show_last: showLast, timings: timings});
    const json = await response.json();
    togglePopoverHandler(document.getElementById(DATE_RANGES_BUTTON_ID), this.panel);
    this._saveDateTimeInFields(json)
  }

  _saveDateTimeInFields(json) {
    const spanElement = document.querySelector(`span[data-filter-type='${this.panelId}']`)
    spanElement.textContent = json['date_interval']
    const fromDateHiddenElement = document.querySelector(`input[name='${this.panelId}[from]']`)
    fromDateHiddenElement.value = json['from_date']
    this._dispatchFieldChangeEvent(fromDateHiddenElement)
    const toDateHiddenElement = document.querySelector(`input[name='${this.panelId}[to]']`)
    toDateHiddenElement.value = json['to_date']
    this._dispatchFieldChangeEvent(toDateHiddenElement)
  }

  _dispatchFieldChangeEvent(field) {
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _castShowLast() {
    if (this.showLastElement.value < 0) {
      this.showLastElement.value = -this.showLastElement.value;
    }
    if (this.showLastElement.value % 1 !== 0) {
      this.showLastElement.value = Math.trunc(this.showLastElement.value);
    }
    this.showLastElement.value = this.showLastElement.value.replace(/[^\d]/, '');
  }

  _initDatePicker() {
    const controller = this;
    return new Litepicker({
      element: controller.fromDateElement,
      elementEnd: controller.toDateElement,
      singleMode: false,
      allowRepick: true,
      inlineMode: true,
      numberOfMonths: NUMBER_OF_MONTHS,
      maxDays: MAX_DAYS,
      maxDate: new Date(),
    });
  }

  _subscribeOnApplyDatePickDateRanges() {
    const applyButtonElements = this.panel.querySelectorAll('.apply-date-ranges-picker-btn');
    const controller = this;
    applyButtonElements.forEach((button) => {
      if (!button.dataset.listenerAttached) {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          controller.sendDatePickerApplyButtonFilters(
            convertDate(controller.pickerObject.getStartDate()),
            convertDate(controller.pickerObject.getEndDate())
          );
        });
        button.dataset.listenerAttached = 'true';
      }
    });
  }

  _subscribeOnLastDateApplyDateRanges() {
    const applyButtonElements = this.panel.querySelectorAll('.apply-last-date-ranges-btn');
    const controller = this;
    applyButtonElements.forEach((button) => {
      if (!button.dataset.listenerAttached) {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const showLast = parseFloat(controller.showLastElement.value)
          const timings = controller.timingsElement.value;
          if (showLast > 0) {
            this.sendLastDateApplyButtonFilters(showLast, timings)
          } else {
            const showLastFromPlaceholder = parseFloat(controller.showLastElement.placeholder);
            this.sendLastDateApplyButtonFilters(showLastFromPlaceholder, timings)
          }
        });
        button.dataset.listenerAttached = 'true';
      }
    });
  }

  disconnect() {
    if (this.pickerObject) {
      this.pickerObject.destroy();
    }
  }
}
